import React, { useState, useMemo, useEffect } from "react";
import Panel from "./Panel";
import "./CompetitiveMarketProfile.css";

const DISTANCE_MILES_OPTIONS = [25, 50, 100, 250, 500];

const makePriceRankOptions = (n) =>
  Array.from({ length: Math.max(1, n) }, (_, i) => ({
    value: `${i + 1} of ${n}`,
    label: `${i + 1} of ${n}`,
  }));
const BLANK_OPTION = { value: "", label: "—" };

// Compute mileage depreciation rate ($/mile) via linear regression on comp data.
// Returns a negative value — higher mileage correlates with lower price.
// Falls back to a price-tier-based industry estimate when regression isn't reliable.
function computeMileageAdjRate(compsWithData, fallbackAvgPrice) {
  const valid = compsWithData.filter(
    (c) => c.mileage != null && c.mileage >= 0 && c.price > 0
  );
  if (valid.length >= 3) {
    const n = valid.length;
    const meanM = valid.reduce((s, c) => s + c.mileage, 0) / n;
    const meanP = valid.reduce((s, c) => s + c.price, 0) / n;
    const numerator = valid.reduce(
      (s, c) => s + (c.mileage - meanM) * (c.price - meanP),
      0
    );
    const denominator = valid.reduce(
      (s, c) => s + (c.mileage - meanM) ** 2,
      0
    );
    if (denominator > 0) {
      const slope = numerator / denominator;
      // Accept slope between -$0.02/mile and -$1.00/mile (plausible automotive range)
      if (slope <= -0.02 && slope >= -1.0) return slope;
    }
  }
  // Fallback: ~1% of vehicle value per 10,000 miles
  const avgP = fallbackAvgPrice ?? 25000;
  return -Math.max(0.05, Math.min(0.50, avgP * 0.0001));
}

function buildVehicleFilterFromApi(vehicleInfo) {
  if (!vehicleInfo || typeof vehicleInfo !== "object") return "";
  const parts = [
    vehicleInfo.year,
    vehicleInfo.make,
    vehicleInfo.model,
    vehicleInfo.trim,
  ].filter(Boolean);
  return parts.join(" ").trim() || "";
}

export default function CompetitiveMarketProfile({ data, mobile = false, hideHooptiePriceAndSoldDom = false }) {
  const vehicleInfo = data?.vehicle_info || {};
  const apiVehicleFilter = buildVehicleFilterFromApi(vehicleInfo);

  const [vehicleFilter, setVehicleFilter] = useState("");
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [distanceMiles, setDistanceMiles] = useState(100);

  useEffect(() => {
    if (apiVehicleFilter) setVehicleFilter(apiVehicleFilter);
  }, [apiVehicleFilter]);

  // Mobile: auto-fill criteria from current vehicle data and run profile once
  useEffect(() => {
    if (!mobile || !data) return;
    const version = data?.search_metadata?.version ?? data?.vehicle_info?.version;
    if (version != null && String(version).trim() !== "") {
      setSelectedVersions([String(version).trim()]);
    }
    setDistanceMiles(100);
  }, [mobile, data]);

  // Mobile: auto-run market profile when data/criteria are set
  useEffect(() => {
    if (!mobile || !data) return;
    const listings = data?.active_listings || data?.market_analysis?.active_listings || [];
    if (listings.length === 0) return;
    handleRefreshMarketProfile();
  }, [mobile, data, distanceMiles, selectedVersions]);

  const normalizedVersion = (v) => (v ? String(v).trim().toLowerCase() : "");
  const listings = data?.active_listings || data?.market_analysis?.active_listings || [];
  const availableVersions = useMemo(() => {
    const seen = new Set();
    return listings
      .map((l) => l.version)
      .filter((v) => v != null && String(v).trim() !== "")
      .filter((v) => {
        const n = normalizedVersion(v);
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      })
      .sort((a, b) => String(a).localeCompare(String(b)));
  }, [listings]);

  const selectedVersionSet = useMemo(
    () => new Set(selectedVersions.map(normalizedVersion)),
    [selectedVersions]
  );
  const versionMatchesListing = (listing) => {
    if (selectedVersionSet.size === 0) return true;
    return selectedVersionSet.has(normalizedVersion(listing.version));
  };

  const toggleVersion = (version) => {
    const n = normalizedVersion(version);
    setSelectedVersions((prev) => {
      const nextSet = new Set(prev.map(normalizedVersion));
      if (nextSet.has(n)) {
        nextSet.delete(n);
      } else {
        nextSet.add(n);
      }
      return availableVersions.filter((v) => nextSet.has(normalizedVersion(v)));
    });
  };
  // Market Profile (right card) – blank until Refresh Market Profile is clicked
  const [profileN, setProfileN] = useState(0);
  const [priceRank, setPriceRank] = useState("");
  const [adjPctToMarket, setAdjPctToMarket] = useState("");
  const [targetRetailPrice, setTargetRetailPrice] = useState("");
  const [marketDaysSupply, setMarketDaysSupply] = useState("");
  const [likeMine, setLikeMine] = useState("");
  const [soldDom, setSoldDom] = useState("");
  const [hooptiePrice, setHooptiePrice] = useState("");
  // Stored from last Refresh for linked Market Profile math
  const [marketAvgStored, setMarketAvgStored] = useState(null);
  const [sortedPricesStored, setSortedPricesStored] = useState([]);
  // Shown when Refresh is clicked but there are fewer than 5 active comps
  const [profileUnavailableMessage, setProfileUnavailableMessage] = useState("");

  const formatCurrencyInput = (val) => {
    const num = val.replace(/\D/g, "");
    if (!num) return "";
    return parseInt(num, 10).toLocaleString();
  };

  const nStored = sortedPricesStored.length;

  const applyAdjPctChange = (pctStr) => {
    const pct = parseFloat(String(pctStr).replace(/%/g, "")) || 0;
    if (marketAvgStored == null || nStored === 0 || profileN <= 0) return;
    const targetNum = Math.round(marketAvgStored * (pct / 100));
    const rank = Math.min(profileN, 1 + sortedPricesStored.filter((p) => p < targetNum).length);
    setTargetRetailPrice(targetNum.toLocaleString());
    setPriceRank(`${rank} of ${profileN}`);
  };

  const applyTargetRetailChange = (targetNum) => {
    if (marketAvgStored == null || marketAvgStored === 0 || nStored === 0 || profileN <= 0) return;
    const pct = Math.round((targetNum / marketAvgStored) * 100);
    setAdjPctToMarket(`${pct}%`);
    const rank = Math.min(profileN, 1 + sortedPricesStored.filter((p) => p < targetNum).length);
    setPriceRank(`${rank} of ${profileN}`);
  };

  const applyRankChange = (rankOneBased) => {
    if (nStored === 0 || profileN <= 0) return;
    const r = Math.max(1, Math.min(nStored, rankOneBased));
    const targetPrice = sortedPricesStored[r - 1];
    if (targetPrice == null) return;
    setTargetRetailPrice(Math.round(targetPrice).toLocaleString());
    if (marketAvgStored != null && marketAvgStored > 0) {
      setAdjPctToMarket(`${Math.round((targetPrice / marketAvgStored) * 100)}%`);
    }
    setPriceRank(`${r} of ${profileN}`);
  };

  const handleTargetPriceChange = (e) => {
    setTargetRetailPrice(formatCurrencyInput(e.target.value));
  };
  const handleTargetPriceBlur = () => {
    const num = parseInt(targetRetailPrice.replace(/,/g, ""), 10);
    if (!Number.isNaN(num) && num > 0) applyTargetRetailChange(num);
  };

  const MARKET_DAYS_SUPPLY_PERIOD_DAYS = 60;
  const SIMILAR_MILEAGE_PCT = 15;

  function getSalesArray(dataSource) {
    if (!dataSource) return [];
    const ma = dataSource.market_analysis;
    const arr = ma?.recent_sales ?? ma?.sold_listings ?? dataSource.recent_sales ?? dataSource.sold_listings ?? [];
    return Array.isArray(arr) ? arr : [];
  }

  function getSoldDate(sale) {
    const dateStr = sale?.last_seen_at_date ?? sale?.sold_date ?? sale?.last_seen_at ?? sale?.sale_date ?? null;
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const isSimilarMileage = (listingMileage, subjectMileage, pct = SIMILAR_MILEAGE_PCT) => {
    if (subjectMileage == null || subjectMileage <= 0) return true;
    const m = listingMileage ?? null;
    if (m == null || m < 0) return false;
    const low = subjectMileage * (1 - pct / 100);
    const high = subjectMileage * (1 + pct / 100);
    return m >= low && m <= high;
  };

  const computeSoldCountLast60Days = (dataSource, versionMatchesFn, radiusMi, subjectMileage = null, requireMileageMatch = true) => {
    const sales = getSalesArray(dataSource);
    const cutoff = new Date(Date.now() - MARKET_DAYS_SUPPLY_PERIOD_DAYS * 24 * 60 * 60 * 1000);
    return sales.filter((sale) => {
      const matchVersion = versionMatchesFn(sale);
      const d = sale.distance_miles ?? sale.distance_mi ?? sale.distance;
      const withinDistance = d == null || d <= radiusMi;
      const saleMiles = sale.miles ?? sale.mileage ?? sale.odometer ?? null;
      const matchMileage = requireMileageMatch ? isSimilarMileage(saleMiles, subjectMileage) : true;
      const saleDate = getSoldDate(sale);
      const withinPeriod = saleDate ? saleDate >= cutoff : true;
      return matchVersion && withinDistance && matchMileage && withinPeriod;
    }).length;
  };

  const computeMarketDaysSupply = (activeCount, soldCount60) => {
    if (soldCount60 <= 0) return "—";
    const dailyRate = soldCount60 / MARKET_DAYS_SUPPLY_PERIOD_DAYS;
    const daysSupply = Math.round(activeCount / dailyRate);
    return `${daysSupply} Days`;
  };

  const computeSoldDom = (dataSource, versionMatchesFn, radiusMi) => {
    const sales = getSalesArray(dataSource);
    const filtered = sales.filter((sale) => {
      const matchVersion = versionMatchesFn(sale);
      const d = sale.distance_miles ?? sale.distance_mi ?? sale.distance;
      const withinDistance = d == null || d <= radiusMi;
      return matchVersion && withinDistance;
    });
    const domValues = filtered.map((s) => s.dom_active ?? s.dom).filter((d) => d != null && d >= 0);
    if (domValues.length === 0) return "—";
    const avg = Math.round(domValues.reduce((s, d) => s + d, 0) / domValues.length);
    return `${avg} Days`;
  };

  const handleRefreshMarketProfile = () => {
    const subjectMileage = data?.vehicle_info?.mileage ?? data?.search_metadata?.mileage ?? null;

    const filtered = listings.filter((listing) => {
      const matchVersion = versionMatchesListing(listing);
      const d = listing.distance_miles ?? listing.distance_mi ?? listing.distance;
      const withinDistance = d == null || d <= distanceMiles;
      return matchVersion && withinDistance;
    });

    const MIN_ACTIVE_COMPS = 5;
    if (filtered.length < MIN_ACTIVE_COMPS) {
      setProfileUnavailableMessage("Market profile is not available due to lack of active listings.");
      setMarketAvgStored(null);
      setSortedPricesStored([]);
      setProfileN(0);
      setPriceRank("");
      setAdjPctToMarket("");
      setTargetRetailPrice("");
      setMarketDaysSupply("");
      setLikeMine("");
      setSoldDom("");
      setHooptiePrice("");
      return;
    }

    setProfileUnavailableMessage("");

    const filteredLikeMine = filtered.filter((listing) => {
      const listingMiles = listing.miles ?? listing.mileage ?? null;
      return isSimilarMileage(listingMiles, subjectMileage);
    });

    const subjectPrice =
      data?.pricing_info?.marketcheck_price ??
      data?.pricing_info?.hooptie_price ??
      data?.search_metadata?.asking_price ??
      null;
    const subjectDom = data?.vehicle_info?.dom ?? data?.search_metadata?.dom ?? null;

    // Build comp price+mileage pairs for normalization
    const compsWithData = filtered
      .map((l) => ({ price: l.price, mileage: l.miles ?? l.mileage ?? null }))
      .filter((c) => c.price != null && c.price > 0);

    // Normalize each comp's price to the subject vehicle's actual mileage so that
    // rank, market average, and adj % to market reflect apples-to-apples comparison.
    let compPrices;
    if (subjectMileage != null && subjectMileage > 0) {
      const rawAvgP =
        compsWithData.length > 0
          ? compsWithData.reduce((s, c) => s + c.price, 0) / compsWithData.length
          : null;
      const ratePerMile = computeMileageAdjRate(compsWithData, rawAvgP);
      compPrices = compsWithData
        .map((c) =>
          c.mileage != null
            ? Math.round(c.price + ratePerMile * (subjectMileage - c.mileage))
            : c.price
        )
        .filter((p) => p > 0);
    } else {
      // No subject mileage available — fall back to raw prices
      compPrices = compsWithData.map((c) => c.price).filter((p) => p > 0);
    }
    const hasSubject = subjectPrice != null && subjectPrice > 0;
    const totalComps = filtered.length;
    const n = totalComps + 1;

    if (compPrices.length === 0 && !hasSubject) {
      setMarketAvgStored(null);
      setSortedPricesStored([]);
      setAdjPctToMarket("100%");
      setProfileN(1);
      setMarketDaysSupply("—");
      setLikeMine("0");
      setSoldDom(computeSoldDom(data, versionMatchesListing, distanceMiles));
      setHooptiePrice(subjectPrice != null ? `$${Number(subjectPrice).toLocaleString()}` : "—");
      setTargetRetailPrice(subjectPrice != null ? Number(subjectPrice).toLocaleString() : "");
      setPriceRank("1 of 1");
      return;
    }

    const allPrices = hasSubject ? [...compPrices, subjectPrice] : [...compPrices];
    allPrices.sort((a, b) => a - b);
    const marketAvg = compPrices.length ? compPrices.reduce((s, p) => s + p, 0) / compPrices.length : subjectPrice;

    const sortedPrices = [...allPrices].sort((a, b) => a - b);
    // At 100% we show target = market avg, so rank = where market avg falls in the sorted list
    const rankAt100Pct = 1 + sortedPrices.filter((p) => p < marketAvg).length;
    const rankAt100PctClamped = Math.max(1, Math.min(n, rankAt100Pct));

    const hideMdsForWideRadius = distanceMiles === 250 || distanceMiles === 500;
    const soldCount60LikeMine = computeSoldCountLast60Days(data, versionMatchesListing, distanceMiles, subjectMileage, true);
    const soldCount60Any = computeSoldCountLast60Days(data, versionMatchesListing, distanceMiles, subjectMileage, false);
    const soldForMds = soldCount60LikeMine > 0 ? soldCount60LikeMine : soldCount60Any;
    const activeForMds = soldCount60LikeMine > 0 ? filteredLikeMine.length : filtered.length;
    const marketDaysSupplyValue = hideMdsForWideRadius ? "—" : computeMarketDaysSupply(activeForMds, soldForMds);

    setMarketAvgStored(marketAvg);
    setSortedPricesStored(sortedPrices);
    setAdjPctToMarket("100%");
    setProfileN(n);
    setPriceRank(`${rankAt100PctClamped} of ${n}`);
    setTargetRetailPrice(Math.round(marketAvg).toLocaleString());
    setMarketDaysSupply(marketDaysSupplyValue);
    setLikeMine(hideMdsForWideRadius ? "—" : String(filteredLikeMine.length));
    setSoldDom(computeSoldDom(data, versionMatchesListing, distanceMiles));
    setHooptiePrice(subjectPrice != null ? `$${Number(subjectPrice).toLocaleString()}` : "—");
  };

  const vehicleFilterDisplay = vehicleFilter || "—";

  const priceRankOptions = useMemo(
    () => (profileN <= 0 ? [BLANK_OPTION] : makePriceRankOptions(profileN)),
    [profileN]
  );
  const activeCount = useMemo(() => {
    return listings.filter((listing) => {
      const matchVersion =
        selectedVersionSet.size === 0 ||
        selectedVersionSet.has(normalizedVersion(listing.version));
      const d = listing.distance_miles ?? listing.distance_mi ?? listing.distance;
      const withinDistance = d == null || d <= distanceMiles;
      return matchVersion && withinDistance;
    }).length;
  }, [listings, distanceMiles, selectedVersionSet]);

  return (
    <div className={`cmp-section ${mobile ? "cmp-section--mobile" : ""}`}>
      <div className="ms-content-grid">
        {!mobile && (
        <div className="ms-left-column">
          <Panel title="Competitive Criteria">
            <div className="cmp-content">
              <div className="cmp-field">
                <label className="cmp-label">Distance</label>
                <select
                  className="cmp-select"
                  value={distanceMiles}
                  onChange={(e) => setDistanceMiles(Number(e.target.value))}
                  aria-label="Distance in miles"
                >
                  {DISTANCE_MILES_OPTIONS.map((mi) => (
                    <option key={mi} value={mi}>{mi} miles</option>
                  ))}
                </select>
              </div>

            <div className="cmp-field">
              <label className="cmp-label">Vehicle Filter</label>
              <div className="cmp-vehicle-main">
                <span className="cmp-vehicle-display">{vehicleFilterDisplay}</span>
              </div>
              <div className="cmp-version-row">
                <label className="cmp-series-label">Version</label>
                <span className="cmp-pill cmp-pill-active">{activeCount} Active</span>
              </div>
              {availableVersions.length > 0 && (
                <div className="cmp-version-checkboxes">
                  {availableVersions.map((ver) => {
                    const isChecked = selectedVersionSet.has(normalizedVersion(ver));
                    return (
                      <label key={ver} className="cmp-version-check">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleVersion(ver)}
                          className="cmp-version-check-input"
                        />
                        <span className="cmp-version-check-label">{ver}</span>
                      </label>
                    );
                  })}
                  {selectedVersions.length > 0 && (
                    <button
                      type="button"
                      className="cmp-version-clear"
                      onClick={() => setSelectedVersions([])}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="cmp-field">
              <label className="cmp-label">Market Mode</label>
              <div className="cmp-market-mode-indicator">
                <span className="cmp-market-mode-dot" />
                <span className="cmp-market-mode-value">Active</span>
              </div>
            </div>

              <button
                type="button"
                className="cmp-refresh-btn"
                onClick={handleRefreshMarketProfile}
              >
                Refresh Market Profile
              </button>
            </div>
          </Panel>
        </div>
        )}

        <div className="ms-right-column">
          <Panel title="Market Profile">
            <div className="cmp-content">
              {profileUnavailableMessage ? (
                <p className="cmp-unavailable-msg">{profileUnavailableMessage}</p>
              ) : null}
              <div className="cmp-inputs-grid">
              <div className="cmp-field">
                <label className="cmp-label">Price Rank</label>
                <select
                  className="cmp-select cmp-input-full"
                  value={priceRank}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPriceRank(val);
                    const match = val.match(/^(\d+)\s+of\s+\d+$/);
                    if (match) applyRankChange(parseInt(match[1], 10));
                  }}
                  aria-label="Price rank"
                >
                  {priceRankOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="cmp-field">
                <label className="cmp-label">Adj % to Market</label>
                <input
                  type="text"
                  className="cmp-input cmp-input-full"
                  value={adjPctToMarket}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/%/g, "").trim();
                    const numeric = raw.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                    setAdjPctToMarket(numeric ? `${numeric}%` : "");
                    if (numeric) applyAdjPctChange(numeric);
                  }}
                  onBlur={(e) => {
                    const raw = e.target.value.replace(/%/g, "").trim();
                    const numeric = raw.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                    if (numeric) {
                      setAdjPctToMarket(`${numeric}%`);
                      applyAdjPctChange(numeric);
                    }
                  }}
                  aria-label="Adj % to market"
                />
                <span className="cmp-helper">95%–105%</span>
              </div>
              <div className="cmp-field cmp-field-full-width">
                <label className="cmp-label">Target Retail Price</label>
                <div className="cmp-currency-wrap">
                  <span className="cmp-currency-prefix">$</span>
                  <input
                    type="text"
                    className="cmp-input cmp-input-full"
                    value={targetRetailPrice}
                    onChange={handleTargetPriceChange}
                    onBlur={handleTargetPriceBlur}
                    aria-label="Target retail price"
                  />
                </div>
              </div>
            </div>

            {/* Readonly computed metrics (from active listings on Refresh). MDS not shown for 250/500 mi. */}
            <div className="cmp-metrics">
              <div className="cmp-metric-row">
                <span className="cmp-metric-label">Market Days Supply</span>
                <span className="cmp-metric-value">{distanceMiles === 250 || distanceMiles === 500 ? "—" : marketDaysSupply}</span>
              </div>
              <div className="cmp-metric-row">
                <span className="cmp-metric-label">Market Days Supply Like Mine</span>
                <span className="cmp-metric-value">{(distanceMiles === 250 || distanceMiles === 500) ? "—" : likeMine}{(distanceMiles !== 250 && distanceMiles !== 500 && likeMine && likeMine !== "—") ? " days" : ""}</span>
              </div>
              {!hideHooptiePriceAndSoldDom && (
                <div className="cmp-metric-row">
                  <span className="cmp-metric-label">Sold DOM</span>
                  <span className="cmp-metric-value">{soldDom}</span>
                </div>
              )}
              {!hideHooptiePriceAndSoldDom && (
                <div className="cmp-metric-row">
                  <span className="cmp-metric-label">Hooptie Price</span>
                  <span className="cmp-metric-value">{hooptiePrice}</span>
                </div>
              )}
              <div className="cmp-metric-row cmp-metric-emphasis">
                <span className="cmp-metric-label">Adj % Cost to Market</span>
                <span className="cmp-metric-value cmp-metric-big">{adjPctToMarket}</span>
              </div>
              <p className="cmp-metric-subtext">Compared to market average</p>
            </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
